import { Smartphone, BellRing, Lock, FileSearch, ShieldCheck, Eye } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const features = [
  {
    icon: Smartphone,
    title: 'File reports from anywhere',
    description:
      'Emergency or non-emergency. Attach photos and video. Device attestation protects the chain of custody so your report is defensible.',
  },
  {
    icon: BellRing,
    title: 'Respond to identity challenges',
    description:
      'If a bank or agency flags an event on your account, you will get a prompt in the NACIS app. Confirm or contest in seconds.',
  },
  {
    icon: Lock,
    title: 'Silent activation when needed',
    description:
      'A quiet duress path for situations where speaking is not safe. Activates without visible sound or screen change.',
  },
  {
    icon: FileSearch,
    title: 'Track your submissions',
    description:
      'See the status of every report you have filed — queued, routed, dispatched, closed — with timestamps and the agency involved.',
  },
  {
    icon: Eye,
    title: 'See what is recorded',
    description:
      'View the device fingerprint, priority score, and jurisdiction attached to your report. Nothing is hidden from the person who filed it.',
  },
  {
    icon: ShieldCheck,
    title: 'Your privacy is contract-level',
    description:
      'Your identity is shared only with the authorities who need it for the specific report. Nothing else is federated or aggregated.',
  },
];

export default function ForCitizens() {
  return (
    <>
      <Hero
        eyebrow="For Citizens"
        title="Report safely. See what happens next. Get identity alerts that matter."
        description="NACIS is the citizen entry point to the federated civic trust fabric — for emergency reports, non-emergency incidents, and identity challenges from banks and issuers."
        primaryCta={{ to: '/contact', label: 'Download the app' }}
        secondaryCta={{ to: '/security', label: 'How your data is handled' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Features"
            title="Designed for real-world moments, not for show."
          />
          <div className="mt-12">
            <FeatureGrid items={features} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="When seconds matter"
        title="Silent activation, duress patterns, and protectee routing."
        description="The NACIS mobile app has a quiet duress path that activates without visible sound or screen change. Silent-activation reports are flagged so reviewers know not to call back until an operator confirms a safe window."
        bullets={[
          'Activation patterns are customisable per user',
          'Silent-activation reports bypass standard queues',
          'Reports involving federally-protected persons are fast-routed to USSS',
        ]}
        image={<MockVisual accent="rose" lines={5} />}
      />

      <Slab
        reverse
        eyebrow="Identity challenges"
        title="Confirm or contest what is being asked about your account."
        description="When a partner institution flags an event — an unusual transaction, an account change, a suspected fraud pattern — you see a challenge in the NACIS app with the context provided, and your response is the source of truth."
        bullets={[
          'Real-time prompts from participating banks, issuers, and agencies',
          'Structured responses feed the IFPS identity state machine',
          'Disputes go through a guided, signed workflow',
        ]}
        image={<MockVisual accent="sky" lines={5} />}
      />

      <CTABanner
        title="Download NACIS for your device."
        description="iOS and Android. Free. Silent activation works offline. Identity challenges require a logged-in account."
        primaryCta={{ to: '/contact', label: 'Get download links' }}
        secondaryCta={{ to: '/privacy', label: 'Read the privacy model' }}
      />
    </>
  );
}
